// Bewerk-formulier voor één blok: naam, type, hoogte, kleur, transparantie,
// lichtdoorlatendheid, verwijderen. Hoort in het linkerpaneel onder de
// bloklijst — selectie en eigenschappen vlak bij elkaar zodat de blik niet
// hoeft te springen.

import { Trash2 } from "lucide-react";
import { useStore } from "../state/store";
import { BLOCK_TYPE_DEFAULTS, type BlockType } from "../types/scenario";
import { Button } from "./ui/Button";
import { Field } from "./ui/Field";
import { InfoBadge } from "./ui/InfoBadge";

export function BlockProperties() {
  const block = useStore((s) =>
    s.selectedBlockId ? s.scenario.blocks.find((b) => b.id === s.selectedBlockId) ?? null : null,
  );
  const updateBlock = useStore((s) => s.updateBlock);
  const removeBlock = useStore((s) => s.removeBlock);

  if (!block) return null;

  const def = BLOCK_TYPE_DEFAULTS[block.type];
  const showHeight = def.heightTop > 0 || block.heightTop > 0 || block.type === "gebouw";
  const showTransmission = block.type === "boombladdek";

  return (
    <section className="p-3 border-t border-border">
      <h2 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
        Eigenschappen
      </h2>

      <Field label="Naam">
        <input
          value={block.name}
          onChange={(e) => updateBlock(block.id, { name: e.target.value })}
          className="w-full bg-background border border-input rounded-md px-2 py-1 text-sm"
        />
      </Field>

      <Field label="Type">
        <select
          value={block.type}
          onChange={(e) => {
            const newType = e.target.value as BlockType;
            const newDef = BLOCK_TYPE_DEFAULTS[newType];
            updateBlock(block.id, {
              type: newType,
              // Pas defaults voor het nieuwe type toe als de gebruiker nog op
              // de oude type-defaults zat — anders niet, om gebruikersinvoer
              // te beschermen.
              heightTop:
                block.heightTop === BLOCK_TYPE_DEFAULTS[block.type].heightTop
                  ? newDef.heightTop
                  : block.heightTop,
              heightBottom:
                block.heightBottom === BLOCK_TYPE_DEFAULTS[block.type].heightBottom
                  ? newDef.heightBottom
                  : block.heightBottom,
              color:
                block.color === BLOCK_TYPE_DEFAULTS[block.type].color
                  ? newDef.color
                  : block.color,
              lightTransmission: newDef.lightTransmission,
            });
          }}
          className="w-full bg-background border border-input rounded-md px-2 py-1 text-sm"
        >
          {Object.entries(BLOCK_TYPE_DEFAULTS).map(([key, d]) => (
            <option key={key} value={key}>
              {d.label}
            </option>
          ))}
        </select>
      </Field>

      {showHeight && (
        <Field label="Hoogte (m)">
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={0}
              max={60}
              step={0.5}
              value={block.heightTop}
              onChange={(e) =>
                updateBlock(block.id, { heightTop: Number(e.target.value) })
              }
              className="flex-1"
            />
            <input
              type="number"
              min={0}
              step={0.1}
              value={block.heightTop}
              onChange={(e) =>
                updateBlock(block.id, { heightTop: Number(e.target.value) })
              }
              className="w-20 bg-background border border-input rounded-md px-2 py-1 text-sm"
            />
          </div>
        </Field>
      )}

      {block.type === "boombladdek" && (
        <Field label="Hoogte onderzijde (m)">
          <input
            type="number"
            min={0}
            step={0.1}
            value={block.heightBottom}
            onChange={(e) =>
              updateBlock(block.id, { heightBottom: Number(e.target.value) })
            }
            className="w-full bg-background border border-input rounded-md px-2 py-1 text-sm"
          />
        </Field>
      )}

      <Field label="Kleur">
        <input
          type="color"
          value={block.color}
          onChange={(e) => updateBlock(block.id, { color: e.target.value })}
          className="w-full h-9 rounded-md border border-input cursor-pointer"
        />
      </Field>

      <Field label={`Transparantie (${block.opacity ?? 60}%)`}>
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={block.opacity ?? 60}
          onChange={(e) => updateBlock(block.id, { opacity: Number(e.target.value) })}
          className="w-full"
        />
      </Field>

      {showTransmission && (
        <Field
          label={
            <span className="flex items-center gap-1">
              Lichtdoorlatendheid ({block.lightTransmission ?? 0}%)
              <InfoBadge text="Het percentage zonlicht dat door het bladdek heen komt. 0% = volledig ondoorlatend (zoals een gebouw), 40% = typische zomerse boomkroon. In de jaarberekening telt elke schaduw-doorgang voor (1 − doorlatendheid)." />
            </span>
          }
          hint="Voor bomen: percentage zonlicht dat door het bladdek heen komt."
        >
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={block.lightTransmission ?? 0}
            onChange={(e) =>
              updateBlock(block.id, { lightTransmission: Number(e.target.value) })
            }
            className="w-full"
          />
        </Field>
      )}

      <div className="pt-3 mt-3 border-t border-border">
        <Button
          variant="destructive"
          size="sm"
          onClick={() => removeBlock(block.id)}
          className="w-full"
        >
          <Trash2 size={14} />
          Verwijder dit blok
        </Button>
      </div>
    </section>
  );
}
